import React from 'react';
import { useIntl } from 'react-intl';
import { Organization } from 'src/models/organization';
import { FormikProps } from 'formik/dist/types';
import { Formik } from 'formik';
import { GenericMessages } from 'src/intl/GenericMessages';
import * as yup from 'yup';
import { BundleMessages } from '../../../intl/BundleMessages';
import { ShareBundleData } from '../../../models/bundle';
import { Roles } from '@dataquiver/stoneage-roles';
import { useAuthUserInfo } from '../../../redux/auth/selectors';
import { AppModal } from 'src/components/reusable-components/app-modal/AppModal';
import { InputSelect } from 'src/components/reusable-components/InputSelect';

interface ShareBundleModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
  /**
   * The user's organizations.
   */
  organizations: Organization[];
  /**
   * Triggers when a form is submitted.
   */
  submitForm: (e: any) => Promise<void>;
}
export const ShareBundleModal: React.FC<ShareBundleModalProps> = ({ onClose, isOpen, organizations, submitForm }) => {
  const user = useAuthUserInfo();
  const { formatMessage } = useIntl();

  const sortedOrganizations = [...organizations].sort((a, b) => {
    return a?.orgName > b.orgName ? 1 : -1;
  });

  const validationSchema = yup.object({
    organizationId: yup.string().required(formatMessage(BundleMessages.requiredFieldMessage)),
  });

  const submitShareReportForm = async (formValues: ShareBundleData) => {
    await submitForm(formValues);
  };

  return (
    <Formik
      initialValues={{
        organizationId: '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values: ShareBundleData) => submitShareReportForm(values)}
    >
      {(formikProps: FormikProps<ShareBundleData>) => (
        <form onSubmit={formikProps.handleSubmit}>
          <AppModal
            isOpen={isOpen}
            title={formatMessage(BundleMessages.shareBundle)}
            onClose={() => {
              onClose();
              formikProps.resetForm();
            }}
            primaryButton={{
              label: formatMessage(BundleMessages.shareBundle),
              onClick: formikProps.handleSubmit,
              disabled: !formikProps.values.organizationId,
            }}
            secondaryButton={{
              label: formatMessage(GenericMessages.cancelButtonLabel),
              onClick: () => {
                onClose();
                formikProps.resetForm();
              },
            }}
          >
            <InputSelect<string>
              label={
                Roles.isStoneAgeDev(user?.role)
                  ? formatMessage(BundleMessages.organizationSelectLabel)
                  : formatMessage(BundleMessages.familyOrganizationSelectLabel)
              }
              value={formikProps.values.organizationId}
              onChange={orgId => formikProps.setFieldValue('organizationId', orgId)}
              onBlur={formikProps.handleBlur}
              options={sortedOrganizations.map(org => ({
                value: org._id,
                label: org.orgName,
              }))}
              name="organizationId"
              id="share-bundle-select-organization"
              required
              // No error text, submit button is disabled until a value is selected
            />
          </AppModal>
        </form>
      )}
    </Formik>
  );
};
export default ShareBundleModal;
